import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { Engine } from './core/Engine';
import { PhysicsWorld } from './core/PhysicsWorld';
import { AudioManager } from './core/AudioManager';
import { VehiclePhysics } from './vehicle/VehiclePhysics';
import { CarModel } from './vehicle/CarModel';
import { MirrorsAndCameras } from './vehicle/MirrorsAndCameras';
import { InputManager } from './input/InputManager';
import { TownBuilder } from './environment/TownBuilder';
import { TrafficManager } from './traffic/TrafficManager';
import { TrajectoryGuide } from './environment/TrajectoryGuide';
import { InfractionLog } from './stvo/InfractionLog';
import { RuleEvaluators } from './stvo/RuleEvaluators';
import { StVOExaminer } from './stvo/StVOExaminer';
import { MissionManager } from './missions/MissionManager';
import { TelemetryRecorder } from './replay/TelemetryRecorder';
import { ReplayEngine } from './replay/ReplayEngine';
import { HUD } from './ui/HUD';
import { CockpitAdjuster } from './ui/CockpitAdjuster';
import { ReplayUI } from './ui/ReplayUI';
import { ExaminerDialog } from './ui/ExaminerDialog';
import { ModeSelectModal } from './ui/ModeSelectModal';
import { ExamResultModal } from './ui/ExamResultModal';
import { SettingsModal } from './ui/SettingsModal';
import { Minimap } from './ui/Minimap';
import { SettingsSyncService, cloneKeyMap } from './core/SettingsSync';

export type GameState = 'MENU' | 'DRIVING' | 'RESULT' | 'REPLAY';

class SimulationApp {
  private engine: Engine;
  private physicsWorld: PhysicsWorld;
  private audioManager: AudioManager;

  private vehiclePhysics!: VehiclePhysics;
  private carModel!: CarModel;
  private mirrorsAndCameras!: MirrorsAndCameras;
  private inputManager: InputManager;

  private townBuilder!: TownBuilder;
  private trafficManager!: TrafficManager;
  public trajectoryGuide!: TrajectoryGuide;

  private infractionLog: InfractionLog;
  private ruleEvaluators!: RuleEvaluators;
  private examiner!: StVOExaminer;
  private missionManager!: MissionManager;

  private telemetryRecorder: TelemetryRecorder;
  private replayEngine: ReplayEngine;

  // UI Components
  private uiRoot: HTMLElement;
  private hud!: HUD;
  private cockpitAdjuster!: CockpitAdjuster;
  private replayUI!: ReplayUI;
  private examinerDialog!: ExaminerDialog;
  private modeSelectModal!: ModeSelectModal;
  private examResultModal: ExamResultModal | null = null;
  private settingsModal!: SettingsModal;
  private minimap!: Minimap;

  private gameState: GameState = 'MENU';
  private _prevHandbrake: boolean = true; // used to detect handbrake ON→OFF transition for auto-shift P→D
  private _debugOverlay: HTMLElement | null = null; // live physics debug panel
  private lastReportedInfractionId: string | null = null;

  constructor() {
    this.uiRoot = document.getElementById('ui-root')!;
    this.engine = new Engine('game-canvas');
    this.physicsWorld = new PhysicsWorld();
    this.audioManager = new AudioManager();
    this.inputManager = new InputManager(this.uiRoot);

    this.telemetryRecorder = new TelemetryRecorder();
    this.replayEngine = new ReplayEngine(this.engine.camera, this.telemetryRecorder);
    this.infractionLog = new InfractionLog();

    this.initScene();
    this.initUI();
    this.setupEventListeners();
    (window as any).__APP__ = this;
    this.animate();
  }

  private initScene() {
    // 1. Build Environment
    this.townBuilder = new TownBuilder(this.engine.scene, this.physicsWorld);
    this.trafficManager = new TrafficManager(this.engine.scene, this.physicsWorld);
    this.trafficManager.setTrafficLights(this.townBuilder.trafficLights);
    this.trajectoryGuide = new TrajectoryGuide();
    this.engine.scene.add(this.trajectoryGuide.group);

    // 2. Setup Vehicle Physics & Model
    this.vehiclePhysics = new VehiclePhysics(this.physicsWorld);
    this.carModel = new CarModel();
    this.engine.scene.add(this.carModel.mesh);

    // 3. Setup Collision Event Listener & Dynamic Shock Feedback
    this.vehiclePhysics.onCollision((event) => {
      // 1. Procedural crash sound synthesis
      this.audioManager.playCollisionSound(event.impactIntensity);

      // 2. Camera trauma & impact jolt
      this.mirrorsAndCameras.addTrauma(event.impactIntensity);

      // 3. StVO § 1 Abs. 2 / § 34 collision evaluation & logging
      this.ruleEvaluators.checkCollision(
        this.missionManager.elapsedTime,
        event,
        this.carModel.mesh.position
      );

      // 4. Voice feedback from Fahrprüfer Müller for significant crashes caused by student
      if (event.impactSpeedKmh > 14.0 && !event.isNpcFault) {
        this.examiner.say('Achtung! Zusammenstoß mit Hindernis! Unfallursache wird protokolliert.', true);
      }
    });

    // 4. Setup Cameras & Live Mirror Render Targets
    this.mirrorsAndCameras = new MirrorsAndCameras(this.engine.camera);
    this.mirrorsAndCameras.bindMirrorMaterials(this.carModel);

    // 5. Setup StVO & Examiner
    this.ruleEvaluators = new RuleEvaluators(this.infractionLog, this.townBuilder, this.trafficManager);
    this.examiner = new StVOExaminer(this.audioManager, this.infractionLog, this.ruleEvaluators);
    this.missionManager = new MissionManager(this.examiner, this.townBuilder);

    // 6. Position vehicle on the Main Boulevard road so the menu background shows the car on the road
    this.vehiclePhysics.resetPosition(new CANNON.Vec3(-2.0, 0.5, 24.0), 0);
    this.mirrorsAndCameras.cameraMode = 'chase';
    this.carModel.update(this.vehiclePhysics, 'off', 0, false, 0.016);
    this.mirrorsAndCameras.update(0.016, this.carModel);
    this.engine.updateSunPosition(this.carModel.mesh.position);
  }


  private initUI() {
    this.hud = new HUD();
    this.hud.mount(this.uiRoot);

    // Mirror canvas overlays (HTML canvases drawn from WebGL render targets)
    this.mirrorsAndCameras.mountMirrorOverlays(this.uiRoot);

    // In-Cockpit Adjustment Floating Panel
    this.cockpitAdjuster = new CockpitAdjuster(this.mirrorsAndCameras);
    this.cockpitAdjuster.mount(this.uiRoot);

    this.cockpitAdjuster.onSettingChanged(() => {
      this.hud.seatMirrorsAdjusted = true;
    });

    this.cockpitAdjuster.onClose(() => {
      this.hud.seatMirrorsAdjusted = true;
    });

    this.settingsModal = new SettingsModal(
      this.inputManager.desktopInput,
      this.audioManager,
      this.mirrorsAndCameras,
      this.inputManager.mobileHUD
    );

    this.hud.onOpenSettings(() => {
      this.settingsModal.mount(this.uiRoot);
    });

    this.hud.onOpenCockpitAdjuster(() => {
      this.cockpitAdjuster.toggle();
    });

    this.hud.onToggleTrajectory(() => {
      const nextVisible = this.trajectoryGuide.toggleVisible();
      this.hud.showInfractionBanner(
        nextVisible ? '🧭 3D-Ideallinie: Aktiviert (Trajectory ON)' : '🧭 3D-Ideallinie: Deaktiviert (Trajectory OFF)',
        2500
      );
    });

    this.hud.onToggleCamera(() => {
      this.mirrorsAndCameras.toggleCameraMode();
      this.hud.setCameraMode(this.mirrorsAndCameras.cameraMode);
    });

    this.settingsModal.onOpenCockpitAdjuster(() => {
      this.cockpitAdjuster.open();
    });

    this.hud.onToggleSeatbelt(() => {
      this.vehiclePhysics.seatbeltFastened = !this.vehiclePhysics.seatbeltFastened;
      this.inputManager.desktopInput.setSeatbelt(this.vehiclePhysics.seatbeltFastened);
    });

    this.hud.onFastenSeatbelt(() => {
      this.vehiclePhysics.seatbeltFastened = true;
      this.inputManager.desktopInput.setSeatbelt(true);
    });

    this.hud.onToggleEngine(() => {
      this.vehiclePhysics.powertrain.toggleEngine();
      if (this.vehiclePhysics.powertrain.engineRunning) {
        this.audioManager.playEngineStart();
      } else {
        this.audioManager.playEngineStop();
      }
    });

    this.hud.onStartEngine(() => {
      this.vehiclePhysics.powertrain.startEngine();
      this.audioManager.playEngineStart();
    });

    this.hud.onToggleHandbrake(() => {
      this.inputManager.toggleHandbrake();
    });

    // Checklist "Lösen" button: ALWAYS releases, never re-engages.
    // This prevents the common UX bug where a student clicks "Lösen" twice and the
    // handbrake ends up back ON right before they try to drive.
    this.hud.onReleaseHandbrake(() => {
      this.inputManager.toggleHandbrake(false); // force OFF
    });

    this.hud.onSelectDriveGear(() => {
      if (this.vehiclePhysics.powertrain.type === 'automatic') {
        this.vehiclePhysics.powertrain.autoGear = 'D';
      } else {
        this.vehiclePhysics.powertrain.manualGear = 1;
      }
    });

    this.hud.onToggleBlinkerLeft(() => {
      this.inputManager.toggleBlinker('left');
    });

    this.hud.onToggleBlinkerRight(() => {
      this.inputManager.toggleBlinker('right');
    });

    this.hud.onToggleMouseSteering(() => {
      this.inputManager.desktopInput.toggleMouseSteering();
    });

    this.inputManager.desktopInput.onMouseSteerStateChange = (active) => {
      this.hud.setMouseSteeringActive(active);
    };

    this.examinerDialog = new ExaminerDialog(this.examiner, this.audioManager);
    this.examinerDialog.mount(this.uiRoot);

    // Initialize Talk-Back / TTS state (OFF by default so student is not distracted)
    const initialSettings = SettingsSyncService.getInstance().getSettings();
    const initialTalkBack = initialSettings.examinerVoiceEnabled ?? false;
    this.applyTalkBackState(initialTalkBack, false);

    this.hud.onToggleTalkBack((enabled) => {
      this.applyTalkBackState(enabled, true);
    });

    this.settingsModal.onToggleTalkBack((enabled) => {
      this.applyTalkBackState(enabled, false);
    });

    // Sync user preferences & physical seat/mirror settings from Database
    SettingsSyncService.getInstance().syncFromDatabase().then((settings) => {
      this.mirrorsAndCameras.driverSettings = { ...settings.driverSettings };
      this.inputManager.desktopInput.keyMap = cloneKeyMap(settings.keyMap);
      this.inputManager.mobileHUD.useGyroSteering = settings.useGyro;
    });

    this.inputManager.mobileHUD.onOpenCockpitAdjuster(() => {
      this.cockpitAdjuster.toggle();
    });

    this.inputManager.mobileHUD.onToggleChecklist(() => {
      this.hud.toggleChecklistCard();
    });

    this.inputManager.mobileHUD.onQuickStart(() => {
      this.hud.confirmAllPreDriveChecks();
    });

    this.hud.onReturnToMenu(() => {
      this.returnToMenu();
    });

    this.replayUI = new ReplayUI(this.replayEngine);

    this.minimap = new Minimap();
    this.minimap.mount(this.uiRoot);

    this.modeSelectModal = new ModeSelectModal();
    this.modeSelectModal.mount(this.uiRoot);

    this.modeSelectModal.onOpenSettings(() => {
      this.settingsModal.mount(this.uiRoot);
    });

    this.modeSelectModal.onStart((config) => {
      this.audioManager.init();
      this.vehiclePhysics.powertrain.setTransmissionType(config.transmission);
      this.missionManager.startMission(config.missionId, this.vehiclePhysics);
      this.inputManager.desktopInput.setHandbrake(true); // Handbrake starts ON
      this.inputManager.desktopInput.setSeatbelt(false); // Seatbelt starts OFF
      this._prevHandbrake = true; // reset edge detector — must be true so release triggers auto-shift
      this.telemetryRecorder.clear();
      this.hud.resetScorePanel();
      this.lastReportedInfractionId = null;

      // Set driving camera mode (spacious chase on mobile, cockpit-driver on desktop)
      const targetCamMode = this.inputManager.isMobile ? 'chase' : 'cockpit-driver';
      this.mirrorsAndCameras.cameraMode = targetCamMode;
      this.hud.setCameraMode(targetCamMode);

      this.carModel.cockpit.updatePhoneScreen(this.missionManager.currentMission.title, 'Altstadt-Zentrum', 100, true);
      this.modeSelectModal.unmount();
      this.gameState = 'DRIVING';
    });

    this.replayUI.onExit(() => {
      this.replayUI.unmount();
      this.modeSelectModal.mount(this.uiRoot);
      this.mirrorsAndCameras.setMirrorOverlaysVisible(false);
      this.gameState = 'MENU';
    });

    this.replayUI.onRetry(() => {
      this.replayUI.unmount();
      this.missionManager.startMission(this.missionManager.currentMission.id, this.vehiclePhysics);
      this.inputManager.desktopInput.setHandbrake(true);
      this.inputManager.desktopInput.setSeatbelt(false);
      this._prevHandbrake = true; // reset edge detector
      this.telemetryRecorder.clear();
      this.hud.resetScorePanel();
      this.lastReportedInfractionId = null;
      this.gameState = 'DRIVING';
    });
  }

  public applyTalkBackState(enabled: boolean, showBanner: boolean = false) {
    this.examiner.setTalkBackEnabled(enabled);
    this.examinerDialog.setTalkBackEnabled(enabled);
    this.hud.setTalkBackEnabled(enabled);
    this.settingsModal.setTalkBackEnabled(enabled);
    if (showBanner) {
      this.hud.showInfractionBanner(
        enabled
          ? '🔊 Prüfer Talk-Back / TTS: AN (Sprachausgabe & Prüfer-Dialog aktiv)'
          : '🔇 Prüfer Talk-Back / TTS: AUS (Nur Richtungs- & Fehleranzeige)',
        2500
      );
    }
  }

  private setupEventListeners() {
    // Audio unlock on user touch/click
    window.addEventListener('pointerdown', () => this.audioManager.init(), { once: true });
    window.addEventListener('keydown', () => this.audioManager.init(), { once: true });

    // ESC → Cancel current exam and return to main menu
    // [K] → Toggle Abfahrkontrolle / Cockpit Check
    // [J] → Toggle Sitz- & Spiegeleinstellung (CockpitAdjuster)
    // [U] → Toggle Prüfer Talk-Back / TTS
    window.addEventListener('keydown', (e) => {
      if (e.code === 'Escape') {
        if (this.gameState === 'DRIVING' || this.gameState === 'REPLAY' || this.gameState === 'RESULT') {
          this.returnToMenu();
        }
        return;
      }
      if (this.gameState === 'DRIVING') {
        if (e.code === 'KeyK') {
          this.hud.toggleChecklistCard();
        } else if (e.code === 'KeyJ') {
          this.cockpitAdjuster.toggle();
        } else if (e.code === 'KeyU') {
          this.applyTalkBackState(!this.examiner.talkBackEnabled, true);
        } else if (e.code === 'KeyO') {
          const nextVisible = !this.carModel.cockpit.isSteeringWheelVisible;
          this.carModel.cockpit.setSteeringWheelVisible(nextVisible);
          this.hud.showInfractionBanner(
            nextVisible ? 'ℹ️ 3D-Lenkrad: Sichtbar' : '🎮 3D-Lenkrad: Ausgeblendet (Sim-Rig Modus)',
            2500
          );
        } else if (e.code === 'KeyT') {
          const nextVisible = this.trajectoryGuide.toggleVisible();
          this.hud.showInfractionBanner(
            nextVisible ? '🧭 3D-Ideallinie: Aktiviert (Trajectory ON)' : '🧭 3D-Ideallinie: Deaktiviert (Trajectory OFF)',
            2500
          );
        }
      }
    });
  }

  private returnToMenu() {
    // Stop audio
    this.audioManager.updateEngine(0, 0, true);
    this.audioManager.playBlinkerTick('off');

    // Clear any running replay
    if (this.gameState === 'REPLAY') {
      this.replayUI.unmount();
    }

    // Clear any result modal
    this.examResultModal?.unmount();
    this.examResultModal = null;

    // Reset vehicle & physics to spawn position on the Main Boulevard road
    this.vehiclePhysics.resetPosition(new CANNON.Vec3(-2.0, 0.5, 24.0), 0);
    this.mirrorsAndCameras.cameraMode = 'chase';
    this.carModel.update(this.vehiclePhysics, 'off', 0, false, 0.016);
    this.mirrorsAndCameras.update(0.016, this.carModel);
    this.engine.updateSunPosition(this.carModel.mesh.position);

    // Reset infractionLog for fresh start
    this.infractionLog.infractions = [];

    // Show main menu
    this.gameState = 'MENU';
    this.mirrorsAndCameras.setMirrorOverlaysVisible(false);
    this.modeSelectModal.mount(this.uiRoot);
  }

  private animate = () => {
    requestAnimationFrame(this.animate);
    const deltaTime = Math.min(this.engine.clock.getDelta(), 0.05);

    if (this.gameState === 'DRIVING') {
      this.updateDriving(deltaTime);
    } else if (this.gameState === 'REPLAY') {
      this.updateReplay(deltaTime);
    } else if (this.gameState === 'MENU') {
      this.townBuilder.update(deltaTime);
      this.trafficManager.update(deltaTime, this.carModel.mesh.position, 0, this.engine.clock.elapsedTime);
      this.carModel.update(this.vehiclePhysics, 'off', 0, false, deltaTime);
      this.mirrorsAndCameras.update(deltaTime, this.carModel);
      this.engine.updateSunPosition(this.carModel.mesh.position);
    }

    this.engine.render();
    if (this.gameState === 'DRIVING') {
      this.mirrorsAndCameras.renderMirrorViewports(this.engine.renderer, this.engine.scene, this.carModel);
    }
  };

  private updateDriving(deltaTime: number) {
    // 1. Process Unified User Inputs
    const input = this.inputManager.update(deltaTime);

    if (input.cameraToggle) {
      this.mirrorsAndCameras.toggleCameraMode();
      this.hud.setCameraMode(this.mirrorsAndCameras.cameraMode);
    }

    if (input.engineRestart) {
      this.vehiclePhysics.powertrain.toggleEngine();
      if (this.vehiclePhysics.powertrain.engineRunning) {
        this.audioManager.playEngineStart();
      } else {
        this.audioManager.playEngineStop();
      }
    }

    if (input.seatbeltToggle) {
      this.vehiclePhysics.seatbeltFastened = !this.vehiclePhysics.seatbeltFastened;
      this.inputManager.desktopInput.setSeatbelt(this.vehiclePhysics.seatbeltFastened);
    }

    if (input.gearShiftUp) {
      this.vehiclePhysics.powertrain.shiftUp(this.vehiclePhysics.speedKmh);
    }
    if (input.gearShiftDown) {
      this.vehiclePhysics.powertrain.shiftDown(this.vehiclePhysics.speedKmh);
    }

    // Apply Gear Shift request
    if (input.gearRequested) {
      if (this.vehiclePhysics.powertrain.type === 'automatic') {
        if (['P', 'R', 'N', 'D'].includes(input.gearRequested)) {
          this.vehiclePhysics.powertrain.autoGear = input.gearRequested as import('./vehicle/Powertrain').AutomaticGear;
        }
      } else {
        const g = parseInt(input.gearRequested, 10);
        if (!isNaN(g)) this.vehiclePhysics.powertrain.manualGear = g;
      }
    }

    // Auto-shift P → D when the handbrake is released in automatic mode.
    // This removes the hidden "press D after Space" step that many users miss.
    // Transition is only triggered on the falling edge (ON → OFF) to avoid re-selecting D on every frame.
    const prevHandbrake = this._prevHandbrake ?? true;
    this._prevHandbrake = input.handbrake;
    if (prevHandbrake && !input.handbrake
      && this.vehiclePhysics.powertrain.type === 'automatic'
      && this.vehiclePhysics.powertrain.autoGear === 'P') {
      this.vehiclePhysics.powertrain.autoGear = 'D';
    }

    // 2. Step Physics Simulation
    this.vehiclePhysics.update(
      deltaTime,
      input.throttle,
      input.brake,
      input.steering,
      input.handbrake,
      input.clutch
    );
    this.physicsWorld.step(deltaTime);

    // 3. Update Car Model Transforms & Visuals

    const isReversing = this.vehiclePhysics.powertrain.autoGear === 'R' || this.vehiclePhysics.powertrain.manualGear === -1;
    this.carModel.update(this.vehiclePhysics, input.blinker, input.brake, isReversing, deltaTime);

    // 4. Update Cameras, Mirrors, and Shoulder Check
    this.mirrorsAndCameras.shoulderCheck.setCheck(input.shoulderCheck);
    this.mirrorsAndCameras.update(deltaTime, this.carModel);
    this.mirrorsAndCameras.renderMirrors(this.engine.renderer, this.engine.scene, this.carModel);

    // Keep mirror overlays visible in all camera views during driving so rear traffic is always visible
    this.mirrorsAndCameras.setMirrorOverlaysVisible(true);

    // Shoulder-check vignette: progress = how far the head has turned (0→1)
    const sc = this.mirrorsAndCameras.shoulderCheck;
    const scProgress = sc.activeCheck ? Math.abs(sc.lookAngleY) / (Math.PI * 0.62) : 0;
    this.hud.showShoulderCheck(sc.activeCheck, scProgress);

    // Update Sun shadow target position
    this.engine.updateSunPosition(this.carModel.mesh.position);

    // 5. Update Town environment (Traffic lights) & AI Traffic & Trajectory Guide
    this.townBuilder.update(deltaTime);
    this.trafficManager.update(
      deltaTime,
      this.carModel.mesh.position,
      Math.abs(this.vehiclePhysics.speedKmh),
      this.missionManager?.elapsedTime || 0
    );
    if (this.trajectoryGuide) {
      const navId = this.missionManager?.currentNavInstruction?.id || '';
      this.trajectoryGuide.update(
        deltaTime,
        this.carModel.mesh.position,
        this.carModel.mesh.rotation.y,
        navId
      );
    }

    // 6. Update Audio Engine
    const isEngineActive = this.vehiclePhysics.powertrain.engineRunning && !this.vehiclePhysics.powertrain.isStalled;
    this.audioManager.updateEngine(
      isEngineActive ? this.vehiclePhysics.powertrain.rpm : 0,
      input.throttle,
      !isEngineActive
    );
    this.audioManager.playBlinkerTick(input.blinker);
    if (input.brake > 0.8 && this.vehiclePhysics.speedKmh > 20) {
      this.audioManager.playBrakeSqueal(input.brake);
    }
    if (input.horn) {
      this.audioManager.playHorn();
    }

    // 7. StVO Rule Evaluations
    const telemetry = this.vehiclePhysics.getTelemetry(
      input.throttle,
      input.brake,
      input.handbrake,
      input.clutch
    );

    this.ruleEvaluators.evaluateAll(
      this.missionManager.elapsedTime,
      telemetry,
      input.blinker,
      input.shoulderCheck,
      input.mirrorLook
    );

    // Check if new infraction occurred (deduped by ID across frames)
    const latestInfraction = this.infractionLog.infractions[this.infractionLog.infractions.length - 1];
    if (latestInfraction && latestInfraction.id !== this.lastReportedInfractionId) {
      this.lastReportedInfractionId = latestInfraction.id;
      this.examiner.reportInfraction(latestInfraction);
      this.hud.showInfractionBanner(latestInfraction.title);
      // Update live score panel and in-cockpit mounted smartphone
      this.hud.logInfraction(latestInfraction, this.infractionLog.totalScore);
      this.carModel.cockpit.updatePhoneScreen(this.missionManager.currentMission.title, 'Altstadt-Zentrum', this.infractionLog.totalScore, true);
    }

    // ── Live active-violation strip ───────────────────────────────────────
    // Show what the student is doing wrong RIGHT NOW (before cooldown fires)
    {
      const pos = this.carModel.mesh.position;
      const speed = Math.abs(telemetry.speedKmh);
      const speedLimit = this.townBuilder.roadNetwork.getSpeedLimitAt(pos);
      let liveMsg: string | null = null;

      // 1. Seatbelt not fastened while moving
      if (speed > 5 && !telemetry.seatbeltFastened) {
        liveMsg = `§ 21a StVO – Sicherheitsgurt nicht angelegt! Bitte sofort anschnallen (Taste B oder HUD 🔒).`;
      }

      // 2. Handbrake still latched while accelerating
      if (!liveMsg && telemetry.handbrake && input.throttle > 0.25) {
        liveMsg = `§ 23 StVO – Handbremse ist noch angezogen! Bitte vor dem Anfahren lösen (Leertaste / HUD 🅿️).`;
      }

      // 3. Trying to drive with engine off
      if (!liveMsg && !telemetry.engineRunning && input.throttle > 0.2) {
        liveMsg = `Motor ist ausgeschaltet! Bitte zuerst anlassen → Taste [I] (Zündung) oder HUD 🔑.`;
      }

      // Speed over limit
      if (!liveMsg && speed > speedLimit + 12) {
        liveMsg = `§ 3 StVO – Zu schnell! ${Math.round(speed)} km/h (erlaubt: ${speedLimit} km/h) – SCHWERER FEHLER`;
      } else if (!liveMsg && speed > speedLimit + 5) {
        liveMsg = `§ 3 StVO – Zu schnell! ${Math.round(speed)} km/h (erlaubt: ${speedLimit} km/h)`;
      }

      // Crossing continuous centre line (Main N-S Boulevard)
      if (!liveMsg) {
        const px = pos.x, pz = pos.z;
        const isMainNsRoad = Math.abs(px) < 4.2 && Math.abs(pz) > 25 && Math.abs(pz) < 110 && Math.abs(pz - 60) > 8 && Math.abs(pz + 60) > 8;
        if (isMainNsRoad) {
          // Northbound (pz > 0): legal right lane is px < 0. Oncoming lane is px > 0.
          // Southbound (pz < 0): legal right lane is px > 0. Oncoming lane is px < 0.
          const isCrossing = (pz > 0 && px > 0.4) || (pz < 0 && px < -0.4);
          if (isCrossing) {
            liveMsg = `§ 41 Zeichen 295 – Durchgezogene Linie überfahren! Sofort zurück auf die rechte Fahrspur.`;
          }
        }
      }

      // Too far right (over road edge / curb) on Main N-S road
      if (!liveMsg && Math.abs(telemetry.speedKmh) > 2) {
        const px = pos.x, pz = pos.z;
        const isMainNsRoad = Math.abs(px) < 4.8 && Math.abs(pz) > 25 && Math.abs(pz) < 110 && Math.abs(pz - 60) > 8 && Math.abs(pz + 60) > 8;
        if (isMainNsRoad) {
          // Northbound: right curb is at px = -4.0. Southbound: right curb is at px = +4.0.
          const isOverCurb = (pz > 0 && px < -3.6) || (pz < 0 && px > 3.6);
          if (isOverCurb) {
            liveMsg = `§ 2 StVO – Zu weit rechts! Fahrbahn vollständig nutzen, nicht über den Rand fahren.`;
          }
        }
      }

      // Approaching RvL junction too fast
      if (!liveMsg) {
        const intersections = [
          { x: 50, z: 60 }, { x: -50, z: 60 }, { x: 50, z: -60 }, { x: -50, z: -60 },
        ];
        for (const inter of intersections) {
          const d = Math.hypot(pos.x - inter.x, pos.z - inter.z);
          if (d < 12 && speed > 26) {
            liveMsg = `§ 8 StVO – Rechts-vor-Links Kreuzung! Bremsbereit fahren (max ~20 km/h). Aktuell: ${Math.round(speed)} km/h.`;
            break;
          }
        }
      }

      this.hud.showActiveViolation(liveMsg);
    }

    // 8. Mission Progress Tracker
    this.missionManager.update(deltaTime, this.vehiclePhysics);

    // 9. Record Telemetry Frame for Instant 3-Camera Replay
    this.telemetryRecorder.record(
      deltaTime,
      {
        position: [this.carModel.mesh.position.x, this.carModel.mesh.position.y, this.carModel.mesh.position.z],
        quaternion: [this.carModel.mesh.quaternion.x, this.carModel.mesh.quaternion.y, this.carModel.mesh.quaternion.z, this.carModel.mesh.quaternion.w],
        speedKmh: telemetry.speedKmh,
        steeringAngle: telemetry.steeringAngle,
        throttle: input.throttle,
        brake: input.brake,
        clutch: input.clutch,
        handbrake: input.handbrake,
        rpm: telemetry.rpm,
        gear: telemetry.gear,
        blinker: input.blinker,
        shoulderCheck: input.shoulderCheck,
        mirrorLook: input.mirrorLook,
        infractionTitle: latestInfraction ? latestInfraction.title : null,
        ...TelemetryRecorder.captureTrafficSnapshot(this.trafficManager),
      },
      this.missionManager.elapsedTime
    );

    // 10. Update UI Overlays (HUD, Mobile Touch Controls, Minimap, Examiner Dialog)
    let activeLightPhase: string | null = null;
    const playerPos = this.carModel.mesh.position;
    const playerQuat = this.carModel.mesh.quaternion;
    const playerFwd = new THREE.Vector3(0, 0, 1).applyQuaternion(playerQuat);

    for (const tl of this.townBuilder.trafficLights) {
      const dist = playerPos.distanceTo(tl.group.position);
      if (dist < 28.0) {
        const approachDir = tl.approachDirection || tl.direction.clone().negate();
        if (playerFwd.dot(approachDir) > 0.45) {
          activeLightPhase = tl.phase;
          break;
        }
      }
    }

    const currentLimit = this.townBuilder.roadNetwork.getSpeedLimitAt(playerPos);
    this.hud.update(telemetry, currentLimit, input.blinker, deltaTime, activeLightPhase);
    this.hud.updateWheelTelemetry(this.inputManager.desktopInput.wheelTelemetry);
    if (this.inputManager.isMobile) {
      this.inputManager.mobileHUD.updateTelemetry(telemetry, this.vehiclePhysics.powertrain.type);
    }

    // 10B. Turn-by-Turn Navigation Instruction Banner (HUD)
    const navInst = this.missionManager.currentNavInstruction;
    if (navInst) {
      this.hud.showNavigationInstruction({
        icon: navInst.icon,
        title: navInst.title,
        text: navInst.text,
        subText: navInst.subText,
        distanceMeters: navInst.distanceMeters,
        onReplay: () => {
          this.audioManager.speakExaminer(navInst.spokenText || navInst.text);
        },
      });
    } else {
      this.hud.showNavigationInstruction(null);
    }

    this.examinerDialog.update();
    this.minimap.render(
      this.carModel.mesh.position,
      this.carModel.mesh.quaternion,
      this.trafficManager.vehicles
    );

    // 11. Check Mission Completion -> Transition to RESULT
    if (this.missionManager.isCompleted && this.missionManager.result) {
      this.gameState = 'RESULT';
      this.audioManager.updateEngine(0, 0, true);
      this.audioManager.playBlinkerTick('off');

      this.examResultModal = new ExamResultModal(
        this.missionManager.result,
        this.infractionLog.infractions
      );
      this.examResultModal.mount(this.uiRoot);

      this.examResultModal.onWatchReplay(() => {
        this.examResultModal?.unmount();
        this.replayEngine.startReplay();
        this.replayUI.mount(this.uiRoot);
        this.gameState = 'REPLAY';
      });

      this.examResultModal.onRetry(() => {
        this.examResultModal?.unmount();
        this.missionManager.startMission(this.missionManager.currentMission.id, this.vehiclePhysics);
        this.telemetryRecorder.clear();
        this.gameState = 'DRIVING';
      });

      this.examResultModal.onMenu(() => {
        this.examResultModal?.unmount();
        this.modeSelectModal.mount(this.uiRoot);
        this.gameState = 'MENU';
      });
    }
  }

  private updateReplay(deltaTime: number) {
    const frame = this.replayEngine.update(deltaTime, this.carModel, this.trafficManager);
    this.replayUI.update(frame);
    this.townBuilder.update(deltaTime);
  }
}

// Start application
window.addEventListener('DOMContentLoaded', () => {
  new SimulationApp();
});
